import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, X, Send, Utensils, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Restaurant() {
    const { activeHotelId } = useAuth();
    const [menu, setMenu] = useState([]);
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [orderType, setOrderType] = useState("dine_in");
    const [roomNumber, setRoomNumber] = useState("");
    const [cart, setCart] = useState([]);
    const [category, setCategory] = useState("all");

    const load = async () => {
        if (!activeHotelId) return;
        const [m, t, o] = await Promise.all([
            api.get(`/restaurant/menu?hotel_id=${activeHotelId}`),
            api.get(`/restaurant/tables?hotel_id=${activeHotelId}`),
            api.get(`/restaurant/orders?hotel_id=${activeHotelId}`),
        ]);
        setMenu(m.data); setTables(t.data); setOrders(o.data);
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const cats = useMemo(() => ["all", ...Array.from(new Set(menu.map(m => m.category)))], [menu]);
    const filteredMenu = category === "all" ? menu : menu.filter(m => m.category === category);

    const addItem = (m) => {
        setCart(prev => {
            const found = prev.find(i => i.menu_item_id === m.id);
            if (found) return prev.map(i => i.menu_item_id === m.id ? { ...i, quantity: i.quantity + 1 } : i);
            return [...prev, { menu_item_id: m.id, name: m.name, price: m.price, quantity: 1 }];
        });
    };
    const decItem = (id) => setCart(prev => prev.flatMap(i => i.menu_item_id === id ? (i.quantity > 1 ? [{ ...i, quantity: i.quantity - 1 }] : []) : [i]));
    const removeItem = (id) => setCart(prev => prev.filter(i => i.menu_item_id !== id));

    const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
    const tax = subtotal * 0.12;
    const total = subtotal + tax;

    const submitOrder = async () => {
        if (!cart.length) return;
        try {
            await api.post("/restaurant/orders", {
                hotel_id: activeHotelId,
                table_id: orderType === "dine_in" ? selectedTable : null,
                order_type: orderType,
                room_number: orderType === "room_service" ? roomNumber : "",
                items: cart,
            });
            toast.success("KOT sent to kitchen");
            setCart([]); setSelectedTable(null); setRoomNumber("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const updateOrder = async (id, status) => {
        try { await api.patch(`/restaurant/orders/${id}`, { status }); toast.success(`Order ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Restaurant POS" subtitle={`${menu.length} menu items · ${orders.filter(o => o.status === "pending" || o.status === "preparing").length} active orders`}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Menu & Tables (left 2 cols) */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Order type & table */}
                    <Card className="p-4 border border-slate-200 rounded-md shadow-none">
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[["dine_in", "Dine In"], ["room_service", "Room Service"], ["takeaway", "Takeaway"], ["delivery", "Delivery"]].map(([k, l]) => (
                                <button key={k} onClick={() => setOrderType(k)}
                                    className={`px-3 py-1.5 text-xs rounded-sm border ${orderType === k ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200"}`}
                                    data-testid={`order-type-${k}`}>{l}</button>
                            ))}
                        </div>
                        {orderType === "dine_in" && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-slate-500">Select Table</Label>
                                <div className="grid grid-cols-6 gap-2 mt-2">
                                    {tables.map(t => (
                                        <button key={t.id} onClick={() => setSelectedTable(t.id)}
                                            className={`px-2 py-2 text-xs rounded border font-mono font-semibold transition-colors ${selectedTable === t.id ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : t.status === "occupied" ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-[#0A1C3E]"}`}
                                            data-testid={`rest-table-${t.name}`}>
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {orderType === "room_service" && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Room Number</Label>
                                <Input value={roomNumber} onChange={e => setRoomNumber(e.target.value)} placeholder="101" className="mt-1 max-w-xs" />
                            </div>
                        )}
                    </Card>

                    {/* Categories */}
                    <div className="flex flex-wrap gap-2">
                        {cats.map(c => (
                            <button key={c} onClick={() => setCategory(c)}
                                className={`px-3 py-1.5 text-xs rounded-sm border ${category === c ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                                data-testid={`menu-cat-${c}`}>
                                {c === "all" ? "All Items" : c}
                            </button>
                        ))}
                    </div>

                    {/* Menu grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {filteredMenu.map(m => (
                            <Card key={m.id} onClick={() => addItem(m)}
                                className="p-3 border border-slate-200 rounded-md shadow-none cursor-pointer hover:border-[#0A1C3E] hover:shadow-md transition-all"
                                data-testid={`menu-item-${m.id}`}>
                                <div className="flex justify-between items-start">
                                    <div className={`w-1.5 h-1.5 rounded-full mt-1 ${m.veg ? "bg-emerald-500" : "bg-red-500"}`} />
                                    <Utensils className="w-3 h-3 text-slate-400" />
                                </div>
                                <div className="mt-2 text-sm font-medium text-slate-900">{m.name}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{m.category}</div>
                                <div className="mt-2 font-mono font-semibold text-slate-900">₹{m.price}</div>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Cart */}
                <Card className="border border-slate-200 rounded-md shadow-none flex flex-col h-fit sticky top-6">
                    <div className="p-4 border-b border-slate-200 bg-slate-50">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Current Order</div>
                        <div className="text-lg font-display font-semibold text-slate-900 mt-1">
                            {orderType === "dine_in" ? (selectedTable ? `Table ${tables.find(t => t.id === selectedTable)?.name}` : "No table") :
                                orderType === "room_service" ? (roomNumber ? `Room ${roomNumber}` : "No room") : orderType.replace("_", " ")}
                        </div>
                    </div>
                    <div className="p-4 flex-1 max-h-96 overflow-auto">
                        {cart.length === 0 && <div className="text-center py-8 text-sm text-slate-400">Empty cart</div>}
                        {cart.map(i => (
                            <div key={i.menu_item_id} className="flex justify-between items-start py-2 border-b border-slate-100" data-testid={`cart-${i.menu_item_id}`}>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-slate-900">{i.name}</div>
                                    <div className="text-xs text-slate-500 font-mono">₹{i.price} × {i.quantity}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => decItem(i.menu_item_id)} className="w-6 h-6 rounded border border-slate-300 hover:bg-slate-100 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                    <div className="w-6 text-center text-sm font-mono">{i.quantity}</div>
                                    <button onClick={() => addItem({ id: i.menu_item_id, name: i.name, price: i.price })} className="w-6 h-6 rounded border border-slate-300 hover:bg-slate-100 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                                    <button onClick={() => removeItem(i.menu_item_id)} className="w-6 h-6 rounded text-red-500 hover:bg-red-50 flex items-center justify-center"><X className="w-3 h-3" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="p-4 border-t border-slate-200 space-y-1 text-sm">
                        <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">₹{subtotal.toFixed(2)}</span></div>
                        <div className="flex justify-between text-slate-600"><span>Tax (12%)</span><span className="font-mono">₹{tax.toFixed(2)}</span></div>
                        <div className="flex justify-between font-semibold text-slate-900 text-base pt-2 border-t border-slate-100"><span>Total</span><span className="font-mono">₹{total.toFixed(2)}</span></div>
                        <Button onClick={submitOrder} disabled={!cart.length || (orderType === "dine_in" && !selectedTable) || (orderType === "room_service" && !roomNumber)}
                            className="w-full mt-3 bg-[#0A1C3E] hover:bg-[#132D5C] rounded-sm" data-testid="submit-kot">
                            <Send className="w-4 h-4 mr-1" /> Send to Kitchen (KOT)
                        </Button>
                    </div>
                </Card>
            </div>

            {/* Active orders */}
            <div className="mt-8">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Active Orders</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {orders.filter(o => !["billed", "cancelled"].includes(o.status)).map(o => (
                        <Card key={o.id} className="p-4 border border-slate-200 rounded-md shadow-none" data-testid={`order-${o.id}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="font-mono font-bold text-sm">{o.kot_number}</div>
                                    <div className="text-xs text-slate-500">{o.order_type?.replace("_", " ")} · ₹{o.total}</div>
                                </div>
                                <span className={`status-badge ${o.status === "pending" ? "bg-red-100 text-red-800 border-red-200" : o.status === "preparing" ? "bg-yellow-100 text-yellow-800 border-yellow-200" : o.status === "ready" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}`}>{o.status}</span>
                            </div>
                            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                                {o.items?.map((i, idx) => <div key={idx}>{i.quantity}× {i.name}</div>)}
                            </div>
                            <div className="mt-3 flex gap-1">
                                {o.status === "pending" && <Button size="sm" variant="outline" onClick={() => updateOrder(o.id, "preparing")} className="h-7 text-xs">Preparing</Button>}
                                {o.status === "preparing" && <Button size="sm" variant="outline" onClick={() => updateOrder(o.id, "ready")} className="h-7 text-xs">Ready</Button>}
                                {o.status === "ready" && <Button size="sm" variant="outline" onClick={() => updateOrder(o.id, "served")} className="h-7 text-xs">Served</Button>}
                                {o.status === "served" && <Button size="sm" onClick={() => updateOrder(o.id, "billed")} className="bg-[#0A1C3E] h-7 text-xs"><Printer className="w-3 h-3 mr-1" /> Bill</Button>}
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </AppShell>
    );
}
